import {
  ChevronDownIcon,
  CloseIcon,
  EditIcon,
  SearchIcon,
  ViewIcon,
  DeleteIcon,
  WarningTwoIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import AppViewDrawer from "components/AppViewDrawer";
import AppViewListItem from "components/AppViewListItem";
import { Pagination } from "components/Pagination";
import { useApi } from "hooks/useApi";
import { useAuthentication } from "hooks/useAuthentication";
import { useUserRole } from "hooks/useUserRole";
import { useCache } from "hooks/useCache";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { MdMap } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Store } from "store";
import { showToast, toastTypes } from "utils";
import { TAX_ADMIN, TAX_COLLECTOR, TAX_SUPER_ADMIN } from "utils/roles";
import { ReactTable } from "../../components/ReactTable";

type Table3RowType = {
  id: number;
  name: string;
  email: string;
  phone_number: string;
  actionHeader: undefined;
  profile_picture: string;
  country_dial_code: string;
  is_active: boolean;
  qr_codes: string;
  lat: string;
  long: string;
};

export default function InstitutionManagementSection() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const cancelRef = useRef<any>();
  const [activeItem, setActiveItem] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { dispatch } = useContext(Store);
  const { hasPermission } = useAuthentication();
  const userRole = useUserRole();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchBarValue34, setSearchBarValue34] = useState("");
  const { getInstitutions, updateInstitution, deleteInstitution } = useApi();
  const { data: institutions, mutate } = useCache("institutions", () =>
    getInstitutions(page, "", searchBarValue34)
  );
  const handleSearch = (event: any) => {
    const value = event.target.value;
    mutate(getInstitutions(page, value));
  };
  const handleClearSearch = (event: any) => {
    setSearchBarValue34("");
    mutate(getInstitutions(page, ""));
  };

  const handleJumpToPage = (pg: number) => {
    setPage(pg);
    mutate(getInstitutions(pg));
  };
  const handleSetNextPage = () => {
    if (institutions.length > 0 && institutions[0].num_of_pages >= page + 1) {
      setPage((prev: any) => prev + 1);
      mutate(getInstitutions(page + 1));
    }
  };
  const handleSetPrevPage = () => {
    if (institutions.length > 0 && page > 0) {
      setPage((prev: any) => prev - 1);
      mutate(getInstitutions(page + 1));
    }
  };

  const handleViewItem = useCallback(
    (item: any) => {
      setActiveItem(item);
      onOpen();
    },
    [onOpen, setActiveItem]
  );

  const handleUpdateActive = useCallback(
    async (institution: any) => {
      // const postData = {...client, address:{...client?.address}, is_active: client?.is_active ? false : true}
      let formData: any = new FormData();
      formData.append("name", institution.name);
      formData.append("first_name", institution.first_name);
      formData.append("last_name", institution.last_name);
      formData.append("phone_number", institution.phone_number);
      formData.append("country_id", institution.country_id);
      formData.append("city_id", institution.city_id);
      formData.append("address", institution.address);
      formData.append("dealer_id", institution.dealer_id);
      formData.append("email", institution.email);
      formData.append("id", institution.id);
      formData.append("is_active", institution?.is_active ? false : true);
      const response = await updateInstitution(formData);
      if (response.id) {
        dispatch(
          showToast({
            toast: toast,
            message: "institution mis à jour avec succès",
            status: toastTypes.success,
          })
        );
        mutate(getInstitutions(page, searchBarValue34));
      }
    },
    [
      dispatch,
      getInstitutions,
      mutate,
      page,
      searchBarValue34,
      toast,
      updateInstitution,
    ]
  );

  // const handleDownloadAll = (downloadLink: string) => {
  //   const pdfUrl = downloadLink;
  //   const link = document.createElement("a");
  //   link.href = pdfUrl;
  //   link.target = "_blank";
  //   link.download = "document.pdf"; // specify the filename
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  const table3Columns = useMemo(() => {
    const table3ColumnHelper = createColumnHelper<Table3RowType>();
    return [
      table3ColumnHelper.accessor("name", {
        cell: (info) => (
          <Box flex={1}>
            <Flex gap="8px" alignItems="center" px="12px" py="0px">
              <Image
                src={info.row.original.profile_picture as unknown as string}
                alt="institution Profile Image"
                h="32px"
                w="32px"
                fit="contain"
                borderRadius="10px"
              />
              <Text>{info.getValue<string>()}</Text>
            </Flex>
          </Box>
        ),
        header: (info) => (
          <Flex flex={1} px={"25px"} py="14px">
            <Heading as="h6">Institution</Heading>
          </Flex>
        ),
        meta: { width: "150px" },
      }),
      table3ColumnHelper.accessor("email", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Email</Heading>
          </Flex>
        ),
        meta: { width: "132px" },
      }),
      table3ColumnHelper.accessor("phone_number", {
        cell: (info) => (
          <Flex px={{ base: "20px", sm: "24px" }}>
            <Text>{info.getValue<string>()}</Text>
          </Flex>
        ),
        header: (info) => (
          <Flex p="14px" px={"40px"}>
            <Heading as="h6">Numéro de téléphone</Heading>
          </Flex>
        ),
        meta: { width: "152px" },
      }),
      table3ColumnHelper.accessor("is_active", {
        cell: (info) => (
          <Flex justifyContent="flex-start" width={"100px"}>
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                size={"sm"}
                colorScheme={
                  info.getValue<boolean>() ? "green_360" : "red_origin"
                }
                borderRadius={"4px"}
                px={"5px"}
                fontSize={"10px"}
              >
                {info.getValue<boolean>() ? "Actif" : "Inactif"}
              </MenuButton>
              <MenuList>
                <MenuItem onClick={() => handleUpdateActive(info.row.original)}>
                  <span>
                    {info.getValue<boolean>() ? "Désactiver" : "Activer"}
                  </span>
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        ),
        header: (info) => (
          <Flex px="15px" py="14px" justifyContent={"flex-start"}>
            <Heading as="h6">État</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
      table3ColumnHelper.accessor("id", {
        cell: (info) => (
          <HStack gap={"2px"} width={"100%"} justifyContent={"flex-end"}>
            {hasPermission("taxpayer", "update") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="Edit"
                size={"sm"}
                icon={<EditIcon />}
                borderRadius={"4px"}
                onClick={() => navigate(`edit/${info.getValue()}`)}
              />
            )}
            {hasPermission("taxpayer", "get") && (
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<ViewIcon />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            )}
            {hasPermission("institution", "delete") && (
              <IconButton
                variant="outline"
                colorScheme="red_origin"
                aria-label="delete"
                size={"sm"}
                icon={<DeleteIcon />}
                borderRadius={"4px"}
                onClick={() => {
                  setActiveItem(info.row.original);
                  onConfirmOpen();
                }}
                isDisabled={isDeleting && activeItem?.id === info.row.original.id}
                isLoading={isDeleting && activeItem?.id === info.row.original.id}
              />
            )}
            <Link
              href={`https://www.google.com/maps/search/?api=1&query=${info.row.original.lat},${info.row.original.long}`}
              target="_blank"
            >
              <IconButton
                variant="outline"
                colorScheme="gray_stroke_0"
                aria-label="view"
                size={"sm"}
                icon={<Icon as={MdMap as any} boxSize={4} />}
                borderRadius={"4px"}
                onClick={() => handleViewItem(info.row.original)}
              />
            </Link>
            {/* {info.row.original?.qr_codes &&
              (userRole === TAX_SUPER_ADMIN ||
                userRole === TAX_ADMIN ||
                userRole === TAX_COLLECTOR) && (
                <IconButton
                  variant="outline"
                  colorScheme="gray_stroke_0"
                  aria-label="view"
                  size={"sm"}
                  icon={<MdDownload />}
                  borderRadius={"4px"}
                  onClick={() => handleDownloadAll(info.row.original?.qr_codes)}
                />
              )} */}
          </HStack>
        ),
        header: (info) => (
          <Flex width={"100%"} justifyContent={"flex-end"} px="15px" py="14px">
            <Heading as="h6">Action</Heading>
          </Flex>
        ),
        meta: { width: "100px" },
      }),
    ];
  }, [navigate, handleViewItem, hasPermission, handleUpdateActive, isDeleting, activeItem, onConfirmOpen]);

  return (
    <>
      <Flex
        flexDirection="column"
        alignItems="start"
        px={{ base: "20px", sm: "24px" }}
        minHeight={"100vh"}
      >
        <Flex gap="4px" alignSelf="stretch" flexDirection="column">
          <Flex
            justifyContent="center"
            alignItems="center"
            flexDirection={{ base: "column", sm: "row" }}
          >
            <Heading size="text2xl" as="h5" fontFamily="Arimo">
              Institutions
            </Heading>
            <Flex
              pl="56px"
              pr="130px"
              gap="13px"
              flex={1}
              justifyContent="flex-end"
              alignItems="center"
              alignSelf={{ base: "stretch", sm: "auto" }}
              flexDirection={{ base: "column", sm: "row" }}
              px={{ md: 0, base: "20px" }}
            >
              {hasPermission("taxpayer", "create") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={
                    <Image
                      src="images/img_handshake_deal_svgrepo_com_red_origin.svg"
                      alt="Handshake-deal Svgrepo.com"
                    />
                  }
                  fontWeight={700}
                  gap="8px"
                  minW="128px"
                  borderRadius="8px"
                  as={Link}
                  href="/institutions/add"
                >
                  Ajouter une institution
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
        <Box
          mt="16px"
          bg="white"
          w={{ md: "100%", base: "100%" }}
          borderRadius="10px"
        >
          <Flex
            bg="white"
            justifyContent="center"
            alignItems="center"
            px="12px"
            py="16px"
            flexDirection={{ md: "row", base: "column" }}
          >
            <Flex
              flex={1}
              alignItems="center"
              alignSelf={{ md: "auto", base: "stretch" }}
              flexDirection={{ base: "column", sm: "row" }}
            >
              <InputGroup w={{ base: "100%", sm: "30%" }}>
                <Input
                  placeholder={`Rechercher une institution`}
                  value={searchBarValue34}
                  onChange={(event: any) =>
                    setSearchBarValue34(event.currentTarget.value)
                  }
                  gap="8px"
                  borderWidth="1px"
                  borderRadius="8px"
                />
                <InputRightElement>
                  {searchBarValue34?.length > 0 ? (
                    <CloseIcon onClick={handleClearSearch} />
                  ) : null}
                </InputRightElement>
              </InputGroup>
              <IconButton
                onClick={handleSearch}
                ml={2}
                borderRadius={"10px"}
                py={"19px"}
                border={"1px solid #e2e4e6"}
                aria-label="search"
                icon={<SearchIcon />}
                justifyContent={"center"}
                alignItems={"center"}
              />
            </Flex>
            <Image
              src="images/img_dot_menu.svg"
              alt="Menu Image"
              h="24px"
              w={{ md: "24px", base: "100%" }}
            />
          </Flex>
          <ReactTable
            size="sm"
            headerCellProps={{ bg: "white" }}
            tableProps={{
              borderColor: "neutral.600",
              borderTopWidth: "1px",
              borderStyle: "solid",
            }}
            columns={table3Columns as any}
            data={institutions}
          />
          <Pagination
            pageIndex={page}
            pageSize={pageSize}
            setNextPageIndex={handleSetNextPage}
            setPrevPageIndex={handleSetPrevPage}
            setPageSize={setPageSize}
            jumpToPage={handleJumpToPage}
            numberOfPages={
              institutions &&
              institutions.length > 0 &&
              institutions[0].num_of_pages
            }
          />
        </Box>
      </Flex>
      <AppViewDrawer isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <DrawerHeader>Fiche institution</DrawerHeader>

        <DrawerBody>
          <VStack alignItems={"flex-start"} gap={"15px"}>
            <HStack>
              {hasPermission("institution", "update") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="green_360"
                  leftIcon={<EditIcon />}
                  fontWeight={700}
                  gap="2px"
                  borderRadius="8px"
                  as={Link}
                  href={`/institutions/edit/${activeItem?.id}`}
                >
                  Modifier
                </Button>
              )}
              {hasPermission("institution", "delete") && (
                <Button
                  size="xl"
                  variant="outline"
                  colorScheme="red_origin"
                  leftIcon={<DeleteIcon />}
                  fontWeight={700}
                  gap="8px"
                  borderRadius="8px"
                  onClick={() => {
                    onConfirmOpen();
                  }}
                  isDisabled={isDeleting}
                  isLoading={isDeleting}
                >
                  Supprimer
                </Button>
              )}
            </HStack>
            <VStack alignItems={"center"} gap={"5px"} width={"100%"}>
              <Flex gap="8px" padding={"1px"}>
                <Image
                  src={activeItem?.profile_picture}
                  fallbackSrc="/images/dummy.png"
                  alt="logo Image"
                  h="100px"
                  w="100px"
                  fit="contain"
                  borderRadius="16px"
                />
              </Flex>
            </VStack>
            <AppViewListItem label="Nom" value={activeItem?.name} />
            <AppViewListItem label="Email" value={activeItem?.email} />
            <AppViewListItem label="Dealer" value={activeItem?.dealer_name} />
            <AppViewListItem label="Address" value={activeItem?.address} />
            <AppViewListItem label="Latitude" value={activeItem?.lat} />
            <AppViewListItem label="Longitude" value={activeItem?.long} />
            <AppViewListItem label="Ville" value={activeItem?.city_name} />
            <AppViewListItem
              label="Numéro de téléphone"
              value={`${activeItem?.phone_number}`}
            />
          </VStack>
        </DrawerBody>
      </AppViewDrawer>
      <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose}>
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="16px" boxShadow="2xl">
            <AlertDialogHeader fontSize="lg" fontWeight="bold" display="flex" alignItems="center" gap={2}>
              <WarningTwoIcon color="red.400" />
              Confirmation de suppression
            </AlertDialogHeader>

            <AlertDialogBody fontSize="xl" color="gray.700" textAlign="center">
              <VStack spacing={3} align="center">
                <WarningTwoIcon boxSize={16} color="red.400" />
                <Text fontSize="xl">
                  Voulez-vous vraiment supprimer l'institution « {activeItem?.name} » ?
                </Text>
                <Text fontSize="xl">
                  Cette action est irréversible.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose} variant="outline" colorScheme="gray_stroke_0" leftIcon={<CloseIcon boxSize={3} />} borderRadius="8px" isDisabled={isDeleting}>
                Annuler
              </Button>
              <Button colorScheme="red_origin" ml={3} leftIcon={<DeleteIcon />} borderRadius="8px" isLoading={isDeleting} loadingText="Suppression..." onClick={async () => {
                if (!activeItem?.id) return;
                try {
                  setIsDeleting(true);
                  await deleteInstitution(activeItem.id);
                  dispatch(
                    showToast({
                      toast,
                      message: "Institution supprimée avec succès",
                      status: toastTypes.success,
                    })
                  );
                  onConfirmClose();
                  onClose();
                  mutate(getInstitutions(page, searchBarValue34));
                } finally {
                  setIsDeleting(false);
                }
              }}>
                Supprimer
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
